import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import BpkButton from '@skyscanner-internal/backpack-web/bpk-component-button';
import {
  withButtonAlignment,
  withRtlSupport,
} from '@skyscanner-internal/backpack-web/bpk-component-icon';
import HeartFilledIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/heart';
import HeartOutlineIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/heart--outline';
import ArrowIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/long-arrow-right';
import { BpkBox } from '@skyscanner-internal/backpack-web/bpk-component-layout';

import { addPageAction, noticeError } from '../../utils';
import CardRail from '../CardRail';
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import InventoryCard from '../InventoryCard';
import { useMBDContext } from '../common/MBD/useMBDContext';

import CardRailWithInventoryCardV2 from './V2/CardRailWithInventoryCardV2';
import useSavedHotels from './useSavedHotels';

import type {
  CardRailWithInventoryCardProps,
  HighestGuestRatingExperimentVariant,
  HighestGuestRatingLayoutVersion,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/CardRailWithInventoryCardProps';

import STYLES from './CardRailWithInventoryCard.module.scss';

const canMeasure = () =>
  typeof performance !== 'undefined' && typeof performance.now === 'function';

/** Closes timing in the same commit, before later sibling hydration. */
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/** Measures client React hydration/commit time, not layout or paint. */
const useCardRailHydrationInstrumentation = ({
  cardCount,
  experimentVariant,
  layoutVersion,
}: {
  cardCount: number;
  experimentVariant?: HighestGuestRatingExperimentVariant;
  layoutVersion: HighestGuestRatingLayoutVersion;
}) => {
  const { rawComponentName } = useMBDContext();
  const [renderStart] = useState(() =>
    canMeasure() ? performance.now() : null,
  );
  const reportedRef = useRef(false);

  useIsomorphicLayoutEffect(() => {
    if (
      !experimentVariant ||
      cardCount === 0 ||
      reportedRef.current ||
      renderStart === null ||
      !canMeasure() ||
      typeof window === 'undefined'
    ) {
      return;
    }

    reportedRef.current = true;
    addPageAction(
      'card_rail_hydration',
      {
        durationMs: performance.now() - renderStart,
        highestGuestRatingExperimentVariant: experimentVariant,
        highestGuestRatingLayoutVersion: layoutVersion,
        component: rawComponentName,
        cardCount,
      },
      window,
    );
  }, [
    cardCount,
    experimentVariant,
    layoutVersion,
    rawComponentName,
    renderStart,
  ]);
};

const reportCardRailError =
  (
    experimentVariant: HighestGuestRatingExperimentVariant,
    layoutVersion: HighestGuestRatingLayoutVersion,
  ) =>
  (error: Error) => {
    if (typeof window === 'undefined') {
      return;
    }

    noticeError(
      error,
      {
        component: 'CardRailWithInventoryCard',
        highestGuestRatingExperimentVariant: experimentVariant,
        highestGuestRatingLayoutVersion: layoutVersion,
      },
      window,
    );
  };

const AlignedArrowIcon = withButtonAlignment(withRtlSupport(ArrowIcon));

const CREATOR_MBD_PARAMS = {
  component_name: 'creator_recommended_hotels',
  string_properties: {
    vertical: 'creators',
    page_type: 'creator-destination',
  },
};

type SharedCardRailProps = Pick<
  CardRailWithInventoryCardProps,
  | 'cards'
  | 'ctaText'
  | 'ctaUrl'
  | 'highestGuestRatingExperimentVariant'
  | 'subtitle'
  | 'title'
> & {
  blankTarget?: boolean;
  showHeartPOC?: boolean;
};

const useSharedCardRailContent = ({
  blankTarget = false,
  cards,
  ctaText,
  ctaUrl,
  showHeartPOC = false,
  subtitle,
  title,
}: SharedCardRailProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();
  const { savedIds, toggle } = useSavedHotels();

  if (!cards.length) {
    return null;
  }

  const mbdComponentName = showHeartPOC
    ? CREATOR_MBD_PARAMS.component_name
    : rawComponentName;

  const callToAction = (
    <BpkButton
      className={STYLES.callToActionButton}
      rel="nofollow"
      href={ctaUrl}
      blank={blankTarget}
      data-tracking-element-id={`${mbdComponentName}_cta`}
      data-tracking-common-params={
        showHeartPOC
          ? JSON.stringify({
              ...CREATOR_MBD_PARAMS,
              component_name: `${mbdComponentName}_cta`,
            })
          : buildMBDParam(`${rawComponentName}_cta`)
      }
    >
      {ctaText}
      <AlignedArrowIcon />
    </BpkButton>
  );

  return {
    content: (
      <CardRail title={title} subtitle={subtitle} callToAction={callToAction}>
        {(layout) =>
          cards.map((card, index: number) => {
            const hotelId = card.href.split('/').pop();
            const trackingIndex = `${layout}_${index}`;

            return showHeartPOC ? (
              <div key={card.title} className={STYLES.heartOverlayWrapper}>
                <InventoryCard
                  {...card}
                  index={trackingIndex}
                  showHeartPOC={showHeartPOC}
                  mbdParams={CREATOR_MBD_PARAMS}
                />
                {hotelId && (
                  <BpkButton
                    aria-label={savedIds.has(hotelId) ? 'Unsave' : 'Save'}
                    className={STYLES.heartOverlayButton}
                    onClick={() => toggle(hotelId)}
                    data-tracking-element-id={`${mbdComponentName}_heart_${trackingIndex}`}
                    data-tracking-common-params={JSON.stringify({
                      ...CREATOR_MBD_PARAMS,
                      component_name: `${mbdComponentName}_heart`,
                    })}
                  >
                    {savedIds.has(hotelId) ? (
                      <HeartFilledIcon
                        className={STYLES.heartOverlayIconSaved}
                      />
                    ) : (
                      <HeartOutlineIcon className={STYLES.heartOverlayIcon} />
                    )}
                  </BpkButton>
                )}
              </div>
            ) : (
              <InventoryCard key={card.title} {...card} index={trackingIndex} />
            );
          })
        }
      </CardRail>
    ),
    trackingProps: {
      'data-tracking-common-params': showHeartPOC
        ? JSON.stringify(CREATOR_MBD_PARAMS)
        : buildMBDParam(rawComponentName),
      'data-tracking-element-id': mbdComponentName,
    },
  };
};

const CardRailWithInventoryCardV1 = ({
  blankTarget = false,
  cards,
  ctaText,
  ctaUrl,
  highestGuestRatingExperimentVariant,
  showHeartPOC = false,
  subtitle,
  title,
}: SharedCardRailProps) => {
  useCardRailHydrationInstrumentation({
    cardCount: cards.length,
    experimentVariant: highestGuestRatingExperimentVariant,
    layoutVersion: 'legacy',
  });

  const sharedContent = useSharedCardRailContent({
    blankTarget,
    cards,
    ctaText,
    ctaUrl,
    showHeartPOC,
    subtitle,
    title,
  });

  if (!sharedContent) {
    return null;
  }

  const { content, trackingProps } = sharedContent;

  return (
    <div className={STYLES.CardRailWithInventoryCard} {...trackingProps}>
      {content}
    </div>
  );
};

export const CardRailWithInventoryCardBpkLayout = ({
  blankTarget = false,
  cards,
  ctaText,
  ctaUrl,
  highestGuestRatingExperimentVariant,
  showHeartPOC = false,
  subtitle,
  title,
}: SharedCardRailProps) => {
  useCardRailHydrationInstrumentation({
    cardCount: cards.length,
    experimentVariant: highestGuestRatingExperimentVariant,
    layoutVersion: 'bpk',
  });

  const sharedContent = useSharedCardRailContent({
    blankTarget,
    cards,
    ctaText,
    ctaUrl,
    showHeartPOC,
    subtitle,
    title,
  });

  if (!sharedContent) {
    return null;
  }

  const { content, trackingProps } = sharedContent;

  return (
    <BpkBox
      width="full"
      data-card-rail-with-inventory-card="true"
      data-testid="CardRailWithInventoryCardBpkLayout"
      {...trackingProps}
    >
      {content}
    </BpkBox>
  );
};

const CardRailWithInventoryCard = ({
  highestGuestRatingExperimentVariant,
  useBpkLayout = false,
  useV2 = false,
  ...rest
}: CardRailWithInventoryCardProps) => {
  if (useV2) {
    return <CardRailWithInventoryCardV2 {...rest} />;
  }

  const effectiveUseBpkLayout = highestGuestRatingExperimentVariant
    ? highestGuestRatingExperimentVariant === 'c'
    : useBpkLayout;
  const layoutVersion = effectiveUseBpkLayout ? 'bpk' : 'legacy';
  const cardRail = effectiveUseBpkLayout ? (
    <CardRailWithInventoryCardBpkLayout
      {...rest}
      highestGuestRatingExperimentVariant={highestGuestRatingExperimentVariant}
    />
  ) : (
    <CardRailWithInventoryCardV1
      {...rest}
      highestGuestRatingExperimentVariant={highestGuestRatingExperimentVariant}
    />
  );

  if (!highestGuestRatingExperimentVariant) {
    return cardRail;
  }

  return (
    <ErrorBoundary
      componentName={
        effectiveUseBpkLayout
          ? 'CardRailWithInventoryCardBpkLayout'
          : 'CardRailWithInventoryCardV1'
      }
      onError={reportCardRailError(
        highestGuestRatingExperimentVariant,
        layoutVersion,
      )}
    >
      {cardRail}
    </ErrorBoundary>
  );
};
export default CardRailWithInventoryCard;
